/* Zoom/focus-gear finder — "I have this camera, which gear fits my lens?"
 *
 * A funnel, not a chart: camera brand → housing → the gears that fit that housing, one card per lens,
 * each resolved to the exact variant so it can go straight into the cart. Deliberately unlike the
 * lens × port compatibility matrix, which answers a different question (what CAN this camera take).
 *
 * The answer comes from assets/dvl-gear.json, built from the EQA catalog — `catn_zoom_gears` scoped by
 * `catn_zg_listings`, so a lens needing several cuts maps each housing to the right SKU. Prices and
 * stock are read from /products/<handle>.js, lazily per visible card, so they are live and in the
 * shopper's market currency and the page does not fire 50 requests on load.
 */
(() => {
  const money = (cents, fmt) =>
    cents == null ? "" : (fmt || "").replace(/\{\{\s*amount[^}]*\}\}/, (cents / 100).toFixed(2)) || (cents / 100).toFixed(2);

  // `shop.money_format` is a theme SETTING, and on some themes it is markup rather than text -
  // Fashionopolism ships "<span class=money>${{amount}} USD</span>" so currency-converter apps can find
  // the price. Through textContent those tags render literally, so a price is the one thing this file
  // writes as HTML: the format is the shop's, the amount is our own digits.
  const setMoney = (node, cents, fmt) => { node.innerHTML = money(cents, fmt); };

  /* One line into Shopify's own analytics bus: the custom pixel subscribes to `all_events` and
     forwards the dvl_* ones to EQA's beacon (see docs/analytics.md). This file therefore has no
     endpoint, no consent handling and no idea where the data goes. With no pixel installed
     `Shopify.analytics` is absent and this is a no-op — and it is wrapped because a storefront must
     never break for a measurement. */
  const track = (name, data) => {
    try {
      // Two transports on purpose. `Shopify.analytics.publish` is for the custom pixel, which is the
      // intended route and the only one that reaches checkout; `EqaBeacon` is the theme-side beacon
      // that carries these today, because a custom pixel cannot be installed by API. With both
      // present the pixel forwards nothing of ours twice — it subscribes to the bus, and EqaBeacon
      // posts directly — but page views WOULD double, which is why installing the pixel means
      // removing the beacon from layout/theme.liquid.
      const a = window.Shopify && window.Shopify.analytics;
      if (a && typeof a.publish === "function") a.publish(name, data || {});
      if (window.EqaBeacon) window.EqaBeacon.send(name, data || {});
    } catch (e) {
      /* never a reason for a page to fail */
    }
  };

  const el = (tag, cls, text) => {
    const n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  };

  const norm = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();

  /** A compact quick-add: a cart glyph plus a "+", the way a collection card's quick-add reads.
   *  The visible words are gone, so the button carries an aria-label AND a title — that label is all a
   *  screen reader or a hover has left, and for a dome it must still say the extension ring goes in
   *  too, which the removed "Add with ring" text used to say out loud. */
  const CART_PATH = "M1.5 2.5h2.2l2.2 9.3a1.8 1.8 0 0 0 1.75 1.4h7.1a1.8 1.8 0 0 0 1.75-1.33L18.5 5.5H5.2";
  const svgNS = "http://www.w3.org/2000/svg";
  const addButton = (label) => {
    const b = document.createElement("button");
    b.type = "button";
    b.className = "dvl-g__btn dvl-g__add";
    b.setAttribute("aria-label", label);
    b.title = label;
    const svg = document.createElementNS(svgNS, "svg");
    svg.setAttribute("class", "dvl-g__add-icon");
    svg.setAttribute("viewBox", "0 0 20 20");
    svg.setAttribute("aria-hidden", "true");
    svg.setAttribute("focusable", "false");
    const path = document.createElementNS(svgNS, "path");
    path.setAttribute("d", CART_PATH);
    path.setAttribute("fill", "none");
    path.setAttribute("stroke", "currentColor");
    path.setAttribute("stroke-width", "1.6");
    path.setAttribute("stroke-linecap", "round");
    path.setAttribute("stroke-linejoin", "round");
    svg.append(path);
    for (const cx of [8.5, 15.2]) {
      const c = document.createElementNS(svgNS, "circle");
      c.setAttribute("cx", String(cx));
      c.setAttribute("cy", "16.6");
      c.setAttribute("r", "1.3");
      c.setAttribute("fill", "currentColor");
      svg.append(c);
    }
    b.append(svg);
    const plus = document.createElement("span");
    plus.className = "dvl-g__add-plus";
    plus.setAttribute("aria-hidden", "true");
    plus.textContent = "+";
    b.append(plus);
    return b;
  };

  /** Once it is in the cart there is no text left to change, so the glyph becomes a tick. */
  const markAdded = (btn, label) => {
    btn.classList.add("is-added");
    btn.setAttribute("aria-label", label);
    btn.title = label;
  };


  // the order the catalog and the compatibility page use — Sony leads because it is the bulk of the
  // range (22 of 56 gear listings); anything unlisted falls in alphabetically after
  const BRAND_ORDER = ["Sony", "Canon", "Fujifilm", "Olympus", "Panasonic", "Nikon"];
  const byOrder = (list) => {
    const rank = (b) => {
      const i = BRAND_ORDER.indexOf(b);
      return i === -1 ? BRAND_ORDER.length : i;
    };
    return [...list].sort((a, b) => rank(a) - rank(b) || a.localeCompare(b));
  };

  class GearFinder {
    constructor(root) {
      this.root = root;
      this.fmt = root.dataset.moneyFormat || "";
      this.rootUrl = (root.dataset.root || "/").replace(/\/+$/, "");
      this.cache = new Map(); // handle → live product JSON
      this.brand = null;
      this.housing = null;
      this.lensBrand = null; // null = every brand; a string = that brand only
      this.lensChosen = false; // until the shopper (or the URL) says otherwise, the default applies
      this.wantHousing = (root.dataset.defaultHousing || "").trim(); // the housing to land on, if any
      this.q = "";
      this.init();
    }

    async init() {
      try {
        const doc = await fetch(this.root.dataset.src, { headers: { Accept: "application/json" } }).then((r) => r.json());
        this.gears = doc.gears || [];
        this.imgs = doc.imgs || [];
        this.generated = doc.generated || "";
      } catch {
        this.root.append(el("p", "dvl-g__empty", "We could not load the gear list just now. Please reload the page."));
        return;
      }
      // every housing the range covers, with its camera brand, from the gears themselves
      const byId = new Map();
      for (const g of this.gears) for (const o of g.o) if (!byId.has(o.h)) byId.set(o.h, { id: o.h, n: o.n, b: o.b });
      this.brands = byOrder(new Set([...byId.values()].map((h) => h.b)));
      const rank = (b) => this.brands.indexOf(b);
      this.housings = [...byId.values()].sort((a, b) => rank(a.b) - rank(b.b) || a.n.localeCompare(b.n));
      // default to the first brand in that order (Sony), so the housing list is usable on arrival
      this.brand = this.brands[0] || null;
      this.defaultHousing = this.resolveHousing(this.wantHousing);

      this.byId = byId;
      this.build();
      track("dvl_finder_opened", { surface: "gear-finder" });
      this.applyUrl();
      // the funnel is in the query string, so Back steps through it instead of leaving the page
      window.addEventListener("popstate", () => {
        this.applyUrl();
      });
    }

    /** The section's `default_housing` setting, matched against the document.
     *
     *  Tried most specific first, because these names overlap: the listing id `A7RV` belongs to a
     *  Sea Frogs housing while `A7RV/A7V` is the Salted Line one, so a "contains" test run first
     *  would answer the wrong housing for a setting of `A7RV`. Anything unmatched is ignored rather
     *  than guessed at, and the finder simply opens on its brand default as before. */
    resolveHousing(want) {
      if (!want) return null;
      const w = want.toLowerCase();
      const hs = this.housings;
      const hit =
        hs.find((h) => h.id === want) ||
        hs.find((h) => h.n === want) ||
        hs.find((h) => h.id.toLowerCase() === w) ||
        hs.find((h) => h.n.toLowerCase() === w) ||
        hs.find((h) => h.n.toLowerCase().endsWith(` ${w}`)) ||
        null;
      return hit ? hit.id : null;
    }

    build() {
      this.root.textContent = "";

      const step = (n, label, field) => {
        const s = el("div", "dvl-g__step");
        const f = el("div", "dvl-g__field");
        f.append(field);
        s.append(el("span", "dvl-g__num", n), el("span", "dvl-g__label", label), f);
        return s;
      };

      // step 1 — brand
      this.chips = el("div", "dvl-g__chips");
      const s1 = step("1", "Your camera brand", this.chips);
      this.s1 = s1;

      // step 2 — housing. A searchable combobox, not a <select>: Sony alone lists 23 housings.
      this.combo = new window.DvlGearCombo({
        placeholder: "Search or choose your housing…",
        emptyText: "No housing matches",
        onChange: (v) => {
          this.housing = v || null;
          // the combo carries the housing's NAME; the id is what every other surface groups by
          track("dvl_housing_chosen", { surface: "gear-finder", housing: this.resolveHousing(v) || "" });
          this.paint();
          this.writeUrl(true);
        },
      });
      const s2 = step("2", "Your underwater housing", this.combo.el);
      this.s2 = s2;

      // step 3 — narrow by lens
      this.search = el("input", "dvl-g__search");
      this.search.type = "search";
      this.search.placeholder = "Filter by lens…";
      this.search.addEventListener("input", () => {
        this.q = norm(this.search.value);
        this.paintResults();
        this.writeUrl(false); // typing must not pile up history entries
      });
      this.lensChips = el("div", "dvl-g__chips dvl-g__chips--filter");
      const lensField = el("div", "dvl-g__lensfield");
      lensField.append(this.search, this.lensChips);
      const s3 = step("3", "Your lens", lensField);
      this.s3 = s3;

      this.count = el("p", "dvl-g__count");
      this.list = el("ul", "dvl-g__list");
      this.toast = el("p", "dvl-g__toast");
      this.toast.hidden = true;
      this.toast.setAttribute("role", "status");

      const steps = el("div", "dvl-g__steps");
      steps.append(s1, s2, s3);
      this.root.append(steps, this.count, this.list, this.toast);
      if (this.generated) {
        this.root.append(el("p", "dvl-g__stamp", `Compatibility data generated ${this.generated.slice(0, 10)}.`));
      }
      this.observer = new IntersectionObserver(
        (entries) => {
          for (const e of entries) {
            if (e.isIntersecting) {
              this.observer.unobserve(e.target);
              this.hydrate(e.target);
            }
          }
        },
        { rootMargin: "200px" },
      );
    }

    paint() {
      this.chips.textContent = "";
      for (const b of this.brands) {
        const c = el("button", "dvl-g__chip", b);
        c.type = "button";
        c.setAttribute("aria-pressed", String(b === this.brand));
        c.addEventListener("click", () => {
          this.brand = this.brand === b ? null : b;
          if (this.brand) track("dvl_brand_chosen", { surface: "gear-finder", brand: this.brand });
          this.housing = null;
          this.lensBrand = null;
          this.paint();
          this.writeUrl(true);
        });
        this.chips.append(c);
      }
      this.s1.dataset.done = this.brand ? "1" : "0";

      const hs = this.housings.filter((h) => !this.brand || h.b === this.brand);
      this.combo.setItems(hs.map((h) => ({ value: h.id, label: h.n })), { keepValue: true });
      this.combo.input.placeholder = this.brand ? "Search or choose your housing…" : "Pick a brand first";
      this.combo.disabled = !this.brand;
      // unconditional: `this.housing` is the single source of truth, so stepping Back to a state with
      // no housing must clear the field too — setItems({keepValue}) would otherwise leave it filled
      this.combo.setValue(this.housing);
      this.s2.dataset.done = this.housing ? "1" : "0";

      this.paintResults();
    }

    /* ---- the query string IS the state ---------------------------------------------------------
       So a chosen housing survives a refresh, the page can be linked or shared mid-funnel, and Back
       steps back through the steps. Values are validated on the way in: a housing that is not in the
       document, or a brand that does not exist, is ignored rather than left to break the render. */

    applyUrl() {
      const p = new URLSearchParams(location.search);
      // A url naming NEITHER a housing nor a brand is the bare page, so the configured default
      // applies. Keying on the url rather than on "is this the first read" keeps the two in step:
      // stepping Back to the bare url shows what the bare url shows.
      const bare = !p.has("housing") && !p.has("brand");
      const h =
        this.byId.get(p.get("housing") || "") ||
        (bare && this.defaultHousing ? this.byId.get(this.defaultHousing) : null);
      const brand = p.get("brand");
      // the housing implies its brand, so it wins over a contradictory ?brand=
      this.brand = h ? h.b : this.brands.includes(brand) ? brand : this.brands[0] || null;
      this.housing = h ? h.id : null;
      // `lens` absent means "not decided", so the default applies; `lens=all` is an explicit All
      const lens = p.get("lens");
      this.lensChosen = lens !== null;
      this.lensBrand = lens === "all" ? null : lens;
      this.q = norm(p.get("q") || "");
      this.search.value = p.get("q") || "";
      this.paint();
    }

    /** `push` for a step the shopper took (so Back undoes it), replace for typing. */
    writeUrl(push) {
      const p = new URLSearchParams(location.search);
      const set = (k, v) => (v ? p.set(k, v) : p.delete(k));
      set("brand", this.brand);
      set("housing", this.housing);
      set("lens", this.lensChosen ? this.lensBrand || "all" : null);
      set("q", this.search.value.trim());
      const qs = p.toString();
      const url = `${location.pathname}${qs ? `?${qs}` : ""}${location.hash}`;
      if (url === location.pathname + location.search + location.hash) return;
      try {
        history[push ? "pushState" : "replaceState"]({ dvlGear: 1 }, "", url);
      } catch {
        /* a sandboxed frame can refuse history writes — the page still works */
      }
    }

    /** Where a card leads.
     *
     *  `variant` is the point of it. Without it Shopify serves the product's DEFAULT variant, and 34 of
     *  the 56 gear listings default to an a6-family housing — so a card for the A7RV opened a page whose
     *  title, price, image and SPO state all said "Sea Frogs CH A6600" until the picker corrected it a
     *  moment later. The finder has already resolved the exact variant (it adds that one to the cart), so
     *  the link may as well say so and let the page render right the first time.
     *
     *  `housing` stays as well, and is not redundant: it is what the picker reads to show its own state,
     *  and it survives a variant the product no longer has. */
    href(g, opt) {
      const q = new URLSearchParams();
      if (opt.vid) q.set("variant", opt.vid);
      q.set("housing", opt.h);
      return `${this.rootUrl}/products/${g.h}?${q}`;
    }

    /** Everything this housing offers, before the lens-brand chips and the search narrow it. */
    forHousing() {
      if (!this.housing) return [];
      const out = [];
      for (const g of this.gears) {
        const opt = g.o.find((o) => o.h === this.housing);
        if (opt) out.push({ g, opt });
      }
      return out.sort((a, b) => a.g.l.localeCompare(b.g.l));
    }


    /** An interned image reference -> its url. `i` is absent when a part has no image, and index 0 is a
     *  real reference, so this tests for a number rather than truthiness. */
    imgOf(o) {
      const i = o && o.i;
      return typeof i === "number" ? this.imgs[i] || "" : "";
    }

    /** The lens brands one listing answers to. Usually one; two when the same lens is sold under two
     *  names (Rokinon is Samyang's US brand) and the store merged them into a single product, so it has
     *  to appear under both chips or half its owners never find it. */
    static brandsOf(g) {
      return g.lbs && g.lbs.length ? g.lbs : [g.lb];
    }

    matches() {
      return this.forHousing().filter(
        ({ g }) =>
          (!this.lensBrand || GearFinder.brandsOf(g).includes(this.lensBrand)) &&
          (!this.q || norm(`${g.l} ${GearFinder.brandsOf(g).join(" ")} ${g.t}`).includes(this.q)),
      );
    }

    /** Lens-brand chips for the brands this housing actually has gears for, with counts, defaulting to
     *  the first available in brand order — the same reason the camera step defaults to Sony: land on a
     *  populated, narrowed result rather than a long mixed list. Because there IS a default, clearing
     *  needs its own control, so an explicit "All" chip leads the row instead of a toggle-off. */
    paintLensChips() {
      const counts = new Map();
      for (const { g } of this.forHousing()) {
        // counted under EVERY brand it answers to, so the chip counts match what the chip then shows
        for (const b of GearFinder.brandsOf(g)) counts.set(b, (counts.get(b) || 0) + 1);
      }
      const brands = byOrder([...counts.keys()]);
      // a brand this housing has no gears for (a stale link, or a housing change) falls back to the
      // default rather than to an empty list
      if (this.lensBrand && !counts.has(this.lensBrand)) {
        this.lensBrand = null;
        this.lensChosen = false;
      }
      if (!this.lensChosen && brands.length > 1) this.lensBrand = brands[0];

      this.lensChips.textContent = "";
      if (brands.length < 2) return; // one brand is not a choice

      const chip = (label, value) => {
        const c = el("button", "dvl-g__chip dvl-g__chip--sm", label);
        c.type = "button";
        c.setAttribute("aria-pressed", String(value === this.lensBrand));
        c.addEventListener("click", () => {
          this.lensBrand = value;
          this.lensChosen = true;
          this.paintResults();
          this.writeUrl(true);
        });
        this.lensChips.append(c);
      };
      chip(`All (${this.forHousing().length})`, null);
      for (const b of brands) chip(`${b} (${counts.get(b)})`, b);
    }

    paintResults() {
      this.paintLensChips();
      const hit = this.matches();
      this.s3.hidden = !this.housing;
      this.s3.dataset.done = this.housing && hit.length ? "1" : "0";
      this.list.textContent = "";

      if (!this.housing) {
        this.count.textContent = "";
        this.list.append(el("li", "dvl-g__empty", "Choose your camera and housing to see the gears that fit it."));
        return;
      }
      const hn = (this.housings.find((h) => h.id === this.housing) || {}).n || this.housing;
      const narrowed = this.lensBrand || this.q;
      this.count.textContent = hit.length
        ? `${hit.length} gear${hit.length === 1 ? " fits" : "s fit"} the ${hn}` +
          (this.lensBrand ? ` from ${this.lensBrand}.` : ".")
        : narrowed
          ? `No gear matches that filter for the ${hn}.`
          : `We have no gear listed for the ${hn} yet.`;
      for (const m of hit) this.list.append(this.card(m));
    }

    card({ g, opt }) {
      const li = el("li", "dvl-g__card");
      li.dataset.handle = g.h;
      li.dataset.vid = opt.vid;

      // the media slot is always present: it is a square ratio box like a collection card's, and an
      // absent image must leave the card the same shape rather than collapsing it.
      // The image belongs to the CUT, not the listing — the builder resolved it by SKU and skipped the
      // collection composites, which is what the listing's featured image often is.
      const shot = this.imgOf(opt);
      if (shot) {
        const img = el("img", "dvl-g__thumb");
        img.src = `${shot}${shot.includes("?") ? "&" : "?"}width=400`;
        img.alt = "";
        img.loading = "lazy";
        li.append(img);
      } else {
        li.append(el("div", "dvl-g__thumb"));
      }

      const body = el("div", "dvl-g__card-body");
      body.append(el("span", "dvl-g__kind", g.t));
      const name = el("h3", "dvl-g__card-name");
      const a = el("a", null, g.l);
      a.href = this.href(g, opt);
      // does the finder actually send people to products? The pixel posts with keepalive, so this
      // survives the navigation it is reporting
      a.addEventListener("click", () => {
        track("dvl_result_clicked", { surface: "gear-finder", housing: opt.h, sku: opt.sku, lens: g.l || "" });
      });
      name.append(a);
      body.append(name, el("span", "dvl-g__sku", opt.sku));

      const foot = el("div", "dvl-g__card-foot");
      const price = el("span", "dvl-g__price dvl-g__price--sm", "—");
      const add = addButton(`Add ${g.t} ${opt.sku} for the ${opt.n} to cart`);
      add.disabled = true;
      add.addEventListener("click", () => this.add(opt, add));
      foot.append(price, add);
      body.append(foot);
      li.append(body);

      li._price = price;
      li._add = add;
      this.observer.observe(li);
      return li;
    }

    /** Live price + stock for one card, on first sight. */
    async hydrate(li) {
      const handle = li.dataset.handle;
      if (!this.cache.has(handle)) {
        this.cache.set(
          handle,
          fetch(`${this.rootUrl}/products/${handle}.js`)
            .then((r) => (r.ok ? r.json() : null))
            .catch(() => null),
        );
      }
      const p = await this.cache.get(handle);
      if (!p) {
        li._price.textContent = "";
        return;
      }
      const v = p.variants.find((x) => String(x.id) === String(li.dataset.vid));
      if (!v) {
        li._price.textContent = "";
        return;
      }
      if (v.available) setMoney(li._price, v.price, this.fmt);
      else li._price.textContent = "Sold out";
      li._add.disabled = !v.available;
      if (!v.available) {
        // the words left the button, so the price slot carries the out-of-stock state
        li._price.classList.add("dvl-g__oos");
        li._add.setAttribute("aria-label", "Sold out");
        li._add.title = "Sold out";
      }
    }

    async add(opt, btn) {
      btn.disabled = true;
      btn.classList.add("is-busy");
      try {
        const r = await fetch(`${this.rootUrl}/cart/add.js`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ items: [{ id: Number(opt.vid), quantity: 1 }] }),
        });
        if (!r.ok) throw new Error(await r.text());
        this.say(`Added ${opt.sku} for the ${opt.n}. <a href="${this.rootUrl}/cart">View cart</a>`);
        btn.classList.remove("is-busy");
        markAdded(btn, `${opt.sku} added to cart`);
        document.dispatchEvent(new CustomEvent("dvl:cart:updated"));
      } catch {
        this.say("That could not be added to the cart. Please try the product page.");
        btn.classList.remove("is-busy");
        btn.disabled = false;
      }
    }

    say(html) {
      this.toast.innerHTML = html;
      this.toast.hidden = false;
    }
  }

  const boot = () => document.querySelectorAll("[data-dvl-gear-finder]").forEach((n) => new GearFinder(n));
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot);
  else boot();
})();
